import Emitter from "es6-event-emitter";
import APIManager from "./../../common/utilities/apimanager";
import SubScriptionManagerFree from "./../../common/subscriptionmanagement/subscriptionmanagerfree";
import SubScriptionManagerSubscription from "./../../common/subscriptionmanagement/subscriptionmanagersubscription";
import { checkReaderSession, requestDownload } from "./subscriptionUtilities";
import paymentMethods from "./paymentMethods";
import { AUTHORIZED } from "./../../common/analytics/analyticsevents";

/**
 * Contstructs the AuthenticatorAuthorisationController
 */
export default class AuthenticatorAuthorisationController extends Emitter {
  _appState = null;

  _sessionManager = null;
  _cookieManager = null;
  _subscriptionManager = null;

  _options = null;

  constructor(appState, sessionManager, cookieManager, options = {}) {
    super();
    this._appState = appState;
    this._sessionManager = sessionManager;
    this._cookieManager = cookieManager;
    this._options = options;
    this._initialiseSubScriptionManager(options);
    this._initListeners();
  }

  /**
   * Returns the current authentication state
   * @return {Boolean}
   */
  get isAuthenticated() {
    return this._subscriptionManager.isAuthenticated;
  }

  logOut() {
    return this._subscriptionManager.logOut();
  }

  authenticate() {
    const { downloadToken } = this._appState;
    return this._subscriptionManager.authenticate(downloadToken);
  }

  authenticateByUserNamePassword(username, password) {
    const { downloadToken } = this._appState;
    return this._subscriptionManager.authenticateByUserNamePassword(
      username,
      password,
      downloadToken
    );
  }

  _authorize(publicationData, confirmDownload) {
    const { downloadToken } = this._appState;
    this._appState.isFreePublication =
      publicationData && publicationData.price === 0;
    //check reader session
    if (
      !checkReaderSession(publicationData, this._cookieManager, this._appState)
    ) {
      const params = {
        sessionManager: this._sessionManager,
        contentPackageId: publicationData.contentPackageId,
        subsId: this._getSubscriptionId(),
        orderId: 0,
        confirmDownload,
      };

      if (this._appState.isFreePublication) {
        // check if free publication
        //make a request download call which will create reader session on success.
        return requestDownload({
          ...params,
          paymentMethod: paymentMethods.FREE,
        });
      } else if (this._appState.timeBasedUnlimitedAccessEnabled) {
        return requestDownload({
          ...params,
          paymentMethod: paymentMethods.TIME_BASED_UNLIMITED_ACCESS,
        })
          .then(() => {
            return Promise.resolve();
          })
          .catch((e) => {
            if (e.data && e.data.StatusMessage == "E27") {
              this._appState.timeBasedUnlimitedAccessEnabled = false;
              return this._subscriptionManager.authorize(
                publicationData,
                downloadToken,
                confirmDownload
              );
            } else {
              return Promise.reject(e);
            }
          });
      }

      return this._subscriptionManager.authorize(
        publicationData,
        downloadToken,
        confirmDownload
      );
    }
    this.checkAuthentication();
    return Promise.resolve();
  }

  checkAuthentication() {
    //check for subscriptionCookie:
    const cookie = this._cookieManager.getCookie(
      this._cookieManager.cookieKeys.LOGIN
    );
    if (cookie) {
      this._subscriptionManager._setLoginInformationFromCookie();
      this._subscriptionManager._authenticated = true;
      this._appState.triggerEvent(AUTHORIZED);
      this._onAuthenticationStatusChanged();
    }
  }

  authorize(publicationData, confirmDownload) {
    return this._authorize(publicationData, confirmDownload).then(() => {
      this._appState.saveReaderSessionCookie();
      return Promise.resolve();
    });
  }

  authorizePage({ contentPackageId, publicationId, pageId }) {
    if (
      this._appState.isFreePublication ||
      this._appState.timeBasedUnlimitedAccessEnabled
    ) {
      return Promise.resolve();
    }
    return this._subscriptionManager.authorizePage(
      contentPackageId,
      publicationId,
      pageId
    );
  }

  authorizeArticle(contentPackageId, publicationId, pageId, contentItemId) {
    if (
      this._appState.isFreePublication ||
      this._appState.timeBasedUnlimitedAccessEnabled
    ) {
      return Promise.resolve();
    }
    return this._subscriptionManager.authorizeArticle(
      contentPackageId,
      publicationId,
      pageId,
      contentItemId
    );
  }

  authorizeByUserNamePassword(
    username,
    password,
    publicationData,
    confirmDownload
  ) {
    const { contentPackageId, publicationId } = publicationData;

    return this.authenticateByUserNamePassword(username, password)
      .then(() =>
        this.authorize({ contentPackageId, publicationId }, confirmDownload)
      )
      .then(() => {
        return { contentPackageId, publicationId };
      });
  }

  /////////

  _initListeners() {
    this._subscriptionManager.on(
      "subscriptionmanager:authenticationstatuschanged",
      this._onAuthenticationStatusChanged.bind(this)
    );
  }

  /**
   * Depending on options/managers instantiates right _subscriptionManager
   * @param  {[String]} subscriptionType
   */
  _initialiseSubScriptionManager({
    subscriptionOptions: { subscriptionType },
  }) {
    switch (subscriptionType) {
      case "free":
        this._subscriptionManager = new SubScriptionManagerFree(
          this._appState,
          this._sessionManager,
          APIManager,
          this._cookieManager,
          this._options
        );
        break;
      case "subscription":
        this._subscriptionManager = new SubScriptionManagerSubscription(
          this._appState,
          this._sessionManager,
          APIManager,
          this._cookieManager,
          this._options
        );
        break;
      case "freemium":
        //TODO
        break;
      case "metered":
        //TODO
        break;
      default:
    }
  }

  _onAuthenticationStatusChanged() {
    this._appState.logedIn = this._subscriptionManager.isAuthenticated;
  }

  _getSubscriptionId() {
    const cookie = this._cookieManager.getCookie(
      this._cookieManager.cookieKeys.LOGIN
    );
    if (cookie) {
      try {
        return JSON.parse(cookie).subsId;
      } catch (error) {
        return 0;
      }
    }
    return 0;
  }

  getSSOState() {
    return this._subscriptionManager.getSSOState();
  }

  createSSOStateCookie(redirectUrl = null) {
    return this._subscriptionManager.createSSOStateCookie(redirectUrl);
  }

  removeSSOStateCookie() {
    this._subscriptionManager.removeSSOStateCookie();
  }
}



// WEBPACK FOOTER //
// ./src/common/subscriptionmanagement/authenticatorAuthorisationController.js